// src/ApiPanel/pages/api/ApiIntegrationRequest.jsx
// Token & API Request -> Api Integration Request.
//
// Against the real endpoint, POST /api/api-integration/, which is one view
// with five actions:
//
//   SUBMIT   remark + optional document   (White label / Admin only)
//   GET      list — Main Admin sees all, others see their own
//   GETBYID  id
//   APPROVE  id                            (Main Admin only)
//   REJECT   id + remark                   (Main Admin only)
//
// Requests are stored as Reports rows with product="api_integration", so a
// row's `status` is the transaction status ("initiate") and `optional_3` is
// the approval state. The list mapper on the server flattens that; this
// screen reads whichever of the two it is given rather than assuming.
//
// Role gating here mirrors the server so people are not offered buttons that
// will be refused — but the server is what enforces it, and it re-checks the
// caller's role on every action regardless of what this screen renders.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge, Card, DataTable, Modal, Notice, ReportPage, Stat, StatGrid,
  count, prettyDate,
} from "../../components/reports/ReportKit";
import { useAction } from "../../hooks/useReportQuery";
import { apiPanelFetch } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

const SUBMIT_ROLES = new Set(["White label", "Admin"]);
const APPROVE_ROLES = new Set(["Main Admin"]);

const MAX_DOC_BYTES = 5 * 1024 * 1024;
const ALLOWED_DOC = [".pdf", ".png", ".jpg", ".jpeg"];

/** The row's approval state, wherever the server put it. */
const stateOf = (row) =>
  String(row.approval_state || row.optional_3 || row.state || row.status || "")
    .toUpperCase();

export default function ApiIntegrationRequest() {
  const { user } = useApiPanelSession();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [detail, setDetail] = useState(null);
  const action = useAction();

  const canSubmit = SUBMIT_ROLES.has(user?.role);
  const canApprove = APPROVE_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.API_INTEGRATION, {
        method: "POST",
        form: { action: "GET" },
      });
      const data = res?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Could not load integration requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    rows.forEach((r) => {
      const s = stateOf(r);
      if (s.includes("APPROV")) c.approved += 1;
      else if (s.includes("REJECT")) c.rejected += 1;
      else c.pending += 1;
    });
    return c;
  }, [rows]);

  const open = async (row) => {
    setDetail({ loading: true });
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.API_INTEGRATION, {
        method: "POST",
        form: { action: "GETBYID", id: row.id },
      });
      setDetail({ loading: false, row: res?.data || row });
    } catch {
      // Fall back to the list row — it already carries most of the fields, and
      // failing to open a detail view should not hide what we do have.
      setDetail({ loading: false, row });
    }
  };

  const decide = async (row, decision) => {
    let remark = "";
    if (decision === "REJECT") {
      remark = window.prompt("Why is this request being rejected?") || "";
      if (remark.trim().length < 3) return;
    } else if (!window.confirm(
      `Approve the integration request from ${row.user_name || row["BC Name"] || "this partner"}?`
    )) {
      return;
    }
    const res = await action.run(
      API_PANEL_ENDPOINTS.API_INTEGRATION,
      { action: decision, id: row.id, remark: remark.trim() },
      { form: true }
    );
    if (res) {
      setDetail(null);
      load();
    }
  };

  return (
    <ReportPage
      title="Api Integration Request"
      subtitle="Requests to have an API integration enabled, and where each one sits in approval."
      actions={
        canSubmit ? (
          <button type="button" className="apip-btn apip-btn-primary" onClick={() => setShowSubmit(true)}>
            <i className="iconoir-plus" /> New Request
          </button>
        ) : null
      }
    >
      {!canSubmit && !canApprove && (
        <Notice tone="info">
          Your role can view integration requests but not raise or approve them.
          Submitting is for White Label and Admin accounts; approval is Main
          Admin only.
        </Notice>
      )}

      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      <StatGrid>
        <Stat label="Total requests" value={count(rows.length)} />
        <Stat label="Pending" value={count(counts.pending)} tone={counts.pending ? "warn" : ""} />
        <Stat label="Approved" value={count(counts.approved)} tone="ok" />
        <Stat label="Rejected" value={count(counts.rejected)} />
      </StatGrid>

      <Card title="Requests">
        <DataTable
          columns={[
            {
              key: "reference",
              label: "Reference",
              render: (r) => <span className="apip-mono">{r.reference_num || r.txn_id || r.number || r.id}</span>,
            },
            {
              key: "user_name",
              label: "Requested by",
              render: (r) => r.user_name || r["BC Name"] || r.name || "—",
            },
            {
              key: "remark",
              label: "Remark",
              render: (r) => {
                const text = r.remark || r.optional_1 || "";
                return text.length > 70 ? <span title={text}>{text.slice(0, 67)}…</span> : text || "—";
              },
            },
            {
              key: "created_at",
              label: "Raised",
              render: (r) => r.created_at || prettyDate(r.date) || "—",
            },
            {
              key: "state",
              label: "Approval",
              render: (r) => <Badge value={stateOf(r) || "PENDING"} />,
            },
            {
              key: "actions",
              label: "",
              width: 190,
              render: (r) => {
                const pending = !/APPROV|REJECT/.test(stateOf(r));
                return (
                  <div className="apip-token-actions">
                    <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost" onClick={() => open(r)}>
                      View
                    </button>
                    {canApprove && pending && (
                      <>
                        <button
                          type="button"
                          className="apip-btn apip-btn-sm apip-btn-primary"
                          disabled={action.busy}
                          onClick={() => decide(r, "APPROVE")}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="apip-btn apip-btn-sm apip-btn-ghost apip-btn-danger"
                          disabled={action.busy}
                          onClick={() => decide(r, "REJECT")}
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty="No integration requests yet."
          rowKey={(r) => r.id}
        />
      </Card>

      <SubmitModal
        open={showSubmit}
        onClose={() => setShowSubmit(false)}
        onDone={() => {
          setShowSubmit(false);
          load();
        }}
      />

      <Modal open={!!detail} title="Integration request" onClose={() => setDetail(null)} wide>
        {detail?.loading && <div className="apip-loading">Loading…</div>}
        {detail?.row && (
          <>
            <dl className="apip-kv apip-kv-2">
              <dt>Reference</dt>
              <dd className="apip-mono">{detail.row.reference_num || detail.row.txn_id || detail.row.number || "—"}</dd>
              <dt>Approval</dt><dd><Badge value={stateOf(detail.row) || "PENDING"} /></dd>
              <dt>Requested by</dt><dd>{detail.row.user_name || detail.row["BC Name"] || "—"}</dd>
              <dt>Raised</dt><dd>{detail.row.created_at || "—"}</dd>
            </dl>
            <div>
              <h4 className="apip-form-subhead">Remark</h4>
              <p className="apip-prose">{detail.row.remark || detail.row.optional_1 || "—"}</p>
            </div>
            {(detail.row.document || detail.row.optional_2) && (
              <Notice tone="info">
                A supporting document was attached. It is not linked here: the
                API stores these under the publicly served media directory, so
                a link on this page would be a link anyone could follow. Ask
                your backend team for it, or see the note in the handover about
                moving these behind an authorised download.
              </Notice>
            )}
          </>
        )}
      </Modal>
    </ReportPage>
  );
}

function SubmitModal({ open, onClose, onDone }) {
  const [remark, setRemark] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = (chosen) => {
    setFileError("");
    if (!chosen) {
      setFile(null);
      return;
    }
    const name = chosen.name.toLowerCase();
    if (!ALLOWED_DOC.some((ext) => name.endsWith(ext))) {
      setFileError(`Attach a ${ALLOWED_DOC.join(", ")} file.`);
      setFile(null);
      return;
    }
    if (chosen.size > MAX_DOC_BYTES) {
      setFileError(`That file is ${(chosen.size / 1024 / 1024).toFixed(1)} MB — keep it under 5 MB.`);
      setFile(null);
      return;
    }
    setFile(chosen);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("action", "SUBMIT");
      body.append("remark", remark.trim());
      if (file) body.append("document", file);
      await apiPanelFetch(API_PANEL_ENDPOINTS.API_INTEGRATION, { method: "POST", body });
      setRemark("");
      setFile(null);
      onDone();
    } catch (err) {
      setError(err.message || "The request could not be submitted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="New API integration request"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            form="apip-integration-form"
            className="apip-btn apip-btn-primary"
            disabled={busy || remark.trim().length < 5}
          >
            {busy ? "Submitting…" : "Submit request"}
          </button>
        </>
      }
    >
      <form id="apip-integration-form" onSubmit={submit}>
        <Notice tone="info">
          Describe what you want integrated and why. A Main Admin reviews it —
          the API says to expect an answer within two business days.
        </Notice>

        <label className="apip-field">
          <span>Remark</span>
          <textarea
            className="apip-input"
            rows={4}
            required
            minLength={5}
            maxLength={500}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Which service, which provider, and what you need enabled"
          />
        </label>

        <label className="apip-field">
          <span>Supporting document (optional)</span>
          <input
            type="file"
            className="apip-input"
            accept={ALLOWED_DOC.join(",")}
            onChange={(e) => pick(e.target.files?.[0])}
          />
          <span className="apip-filter-hint">
            PDF or image, up to 5 MB. Do not attach anything containing
            credentials — uploads are stored in the public media directory.
          </span>
        </label>

        {file && <p className="apip-hint">Attached: {file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
        {fileError && <Notice tone="bad">{fileError}</Notice>}
        {error && <Notice tone="bad">{error}</Notice>}
      </form>
    </Modal>
  );
}
